import React, { useState } from 'react';
import ElementRenderer from './elements/ElementRenderer';
import { getElementTypeLabel } from '../utils/translations';
import TemplateCellIndicator from './templates/TemplateCellIndicator';

// Composant pour les cellules imbriquées
function NestedGridCell({
                            cell,
                            parentRowIndex,
                            parentCellIndex,
                            nestedRowIndex,
                            nestedCellIndex,
                            totalColumns,
                            nestedLevel = 1,
                            selectedElement,
                            selectedCell,
                            onSelectElement,
                            onSelectNestedCell,
                            onDeleteElement,
                            onClearNestedCell,
                            onDeleteNestedColumn,
                            onMoveNestedColumn,
                            onDragOver,
                            onElementDragStart,
                            onElementDrop,
                            onSelectCell,
                            onSelectRow,
                            previewMode,
                            previewMember,
                            demoAssets,
                            isReadOnly = false
                        }) {
    const [isDragOver, setIsDragOver] = useState(false);

    const isSelected = selectedCell?.isNested &&
        selectedCell?.parentRowIndex === parentRowIndex &&
        selectedCell?.parentCellIndex === parentCellIndex &&
        selectedCell?.nestedRowIndex === nestedRowIndex &&
        selectedCell?.nestedCellIndex === nestedCellIndex;

    // Calculer les bordures personnalisées
    const borderWidth = cell.style?.borderWidth || '1px';
    const borderColor = cell.style?.borderColor || '#e0e0e0';
    const hasBorderTop = cell.style?.borderTop;
    const hasBorderRight = cell.style?.borderRight;
    const hasBorderBottom = cell.style?.borderBottom;
    const hasBorderLeft = cell.style?.borderLeft;
    const hasAnyBorder = hasBorderTop || hasBorderRight || hasBorderBottom || hasBorderLeft;

    const style = {
        // Si aucune bordure personnalisée, utiliser la bordure par défaut en mode édition
        border: hasAnyBorder ? 'none' : '1px solid #e0e0e0',
        borderTop: hasBorderTop ? `${borderWidth} solid ${borderColor}` : (hasAnyBorder ? 'none' : undefined),
        borderRight: hasBorderRight ? `${borderWidth} solid ${borderColor}` : (hasAnyBorder ? 'none' : undefined),
        borderBottom: hasBorderBottom ? `${borderWidth} solid ${borderColor}` : (hasAnyBorder ? 'none' : undefined),
        borderLeft: hasBorderLeft ? `${borderWidth} solid ${borderColor}` : (hasAnyBorder ? 'none' : undefined),
        paddingTop: cell.style?.paddingTop || '10px',
        paddingRight: cell.style?.paddingRight || '10px',
        paddingBottom: cell.style?.paddingBottom || '10px',
        paddingLeft: cell.style?.paddingLeft || '10px',
        verticalAlign: cell.style?.verticalAlign || 'top',
        backgroundColor: cell.style?.backgroundColor || undefined,
        minHeight: '60px',
        position: 'relative',
        width: cell.style?.width || 'auto',
        transition: 'all 0.2s'
    };

    // Fonction pour sélectionner un élément ET désélectionner la cellule/ligne
    const handleSelectElement = (element) => {
        if (onSelectCell) {
            onSelectCell(null); // Désélectionner la cellule
        }
        if (onSelectRow) {
            onSelectRow(null); // Désélectionner la ligne
        }
        onSelectElement(element);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragOver(false);
        }
    };

    const handleDragOverLocal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver(e);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        // Empêcher le drop si la cellule est en lecture seule (template)
        if (isReadOnly) {
            return;
        }

        try {
            const data = e.dataTransfer.getData('application/json');
            if (data) {
                const dragData = JSON.parse(data);

                if (dragData.type === 'existing-element' && onElementDrop) {
                    onElementDrop(dragData, parentRowIndex, parentCellIndex, nestedRowIndex, nestedCellIndex);
                    return;
                }
            }
        } catch (err) {}

        const event = new CustomEvent('nestedCellDrop', {
            detail: {
                parentRowIndex,
                parentCellIndex,
                nestedRowIndex,
                nestedCellIndex,
                originalEvent: e
            },
            bubbles: true
        });
        e.target.dispatchEvent(event);
    };

    const handleSelectNestedCell = (e) => {
        e.stopPropagation();
        if (onSelectElement) {
            onSelectElement(null); // Désélectionner l'élément d'abord
        }
        if (onSelectNestedCell) {
            onSelectNestedCell({
                parentRowIndex,
                parentCellIndex,
                nestedRowIndex,
                nestedCellIndex,
                isNested: true
            });
        }
    };

    const handleClearNestedCell = (e) => {
        e.stopPropagation();
        if (onClearNestedCell) {
            onClearNestedCell(parentRowIndex, parentCellIndex, nestedRowIndex, nestedCellIndex);
        }
    };

    // NOUVEAU: Gérer la suppression de colonne imbriquée
    const handleDeleteNestedColumn = (e) => {
        e.stopPropagation();
        if (onDeleteNestedColumn) {
            onDeleteNestedColumn(parentRowIndex, parentCellIndex, nestedRowIndex, nestedCellIndex);
        }
    };

    // NOUVEAU: Gérer le déplacement de colonne imbriquée
    const handleMoveNestedColumn = (e, direction) => {
        e.stopPropagation();
        if (onMoveNestedColumn) {
            onMoveNestedColumn(parentRowIndex, parentCellIndex, nestedRowIndex, nestedCellIndex, direction);
        }
    };

    return (
        <td
            className={`grid-cell ${isSelected && !previewMode ? 'selected' : ''} ${isDragOver && !previewMode ? 'drag-over' : ''} ${previewMode ? 'preview-mode' : ''}`}
            style={style}
            onClick={!previewMode ? handleSelectNestedCell : undefined}
            onDragEnter={!previewMode ? handleDragEnter : undefined}
            onDragLeave={!previewMode ? handleDragLeave : undefined}
            onDragOver={!previewMode ? handleDragOverLocal : undefined}
            onDrop={!previewMode ? handleDrop : undefined}
        >
            {!previewMode && (
                <button
                    className="cell-selector-btn"
                    onClick={handleSelectNestedCell}
                    style={{ fontSize: '10px', padding: '2px 8px' }}
                >
                    Cellule {nestedLevel > 1 ? `(N${nestedLevel})` : ''}
                </button>
            )}

            {!previewMode && !isReadOnly && (
                <div className="cell-actions">
                {/* Déplacement horizontal */}
                {nestedCellIndex > 0 && (
                    <button
                        className="cell-action-btn"
                        onClick={(e) => handleMoveNestedColumn(e, 'left')}
                        title="Déplacer à gauche"
                    >
                        ←
                    </button>
                )}
                {nestedCellIndex < totalColumns - 1 && (
                    <button
                        className="cell-action-btn"
                        onClick={(e) => handleMoveNestedColumn(e, 'right')}
                        title="Déplacer à droite"
                    >
                        →
                    </button>
                )}
                {/* Actions de cellule */}
                <button
                    className="cell-action-btn danger"
                    onClick={handleClearNestedCell}
                    title="Vider la cellule imbriquée"
                >
                    🗑️
                </button>
                <button
                    className="cell-action-btn danger"
                    onClick={handleDeleteNestedColumn}
                    title="Supprimer la colonne"
                >
                    ⊖
                </button>
                </div>
            )}

            {cell.elements?.length === 0 && !cell.nestedGrid && !previewMode && (
                <div className="drop-zone" style={{ minHeight: '40px' }}>
                    Glissez un élément ici
                </div>
            )}

            {cell.elements?.map(element => (
                <div
                    key={element.id}
                    className={`canvas-element ${selectedElement?.id === element.id && !previewMode ? 'selected' : ''} ${isReadOnly ? 'readonly' : ''}`}
                    draggable={!previewMode && !isReadOnly}
                    onDragStart={!previewMode && !isReadOnly ? (e) => onElementDragStart && onElementDragStart(e, element, parentRowIndex, parentCellIndex, nestedRowIndex, nestedCellIndex) : undefined}
                    onClick={!previewMode && !isReadOnly ? (e) => { e.stopPropagation(); handleSelectElement(element); } : undefined}
                    style={{ cursor: isReadOnly ? 'default' : undefined }}
                >
                    {!previewMode && <span className="element-label">{getElementTypeLabel(element.type)}</span>}
                    {!previewMode && !isReadOnly && (
                        <button
                            className="element-delete"
                            onClick={(e) => { e.stopPropagation(); onDeleteElement(element.id); }}
                        >
                            ×
                        </button>
                    )}
                    <ElementRenderer element={element} previewMode={previewMode} previewMember={previewMember} demoAssets={demoAssets} />
                </div>
            ))}

            {cell.nestedGrid && (
                <div style={{
                    marginTop: previewMode ? '0' : '10px',
                    border: previewMode ? 'none' : '1px dashed #999',
                    padding: previewMode ? '0' : '8px',
                    borderRadius: previewMode ? '0' : '4px',
                    background: previewMode ? 'transparent' : '#f5f5f5'
                }}>
                    {cell.nestedGrid.map((deepNestedRow, deepNestedIndex) => (
                        <table key={deepNestedIndex} style={{ width: '100%', borderCollapse: 'collapse', marginBottom: previewMode ? '0' : '8px' }}>
                            <tbody>
                            <tr>
                                {deepNestedRow.cells.map((deepNestedCell, deepNestedCellIndex) => (
                                    <NestedGridCell
                                        key={deepNestedCellIndex}
                                        cell={deepNestedCell}
                                        parentRowIndex={parentRowIndex}
                                        parentCellIndex={parentCellIndex}
                                        nestedRowIndex={deepNestedIndex}
                                        nestedCellIndex={deepNestedCellIndex}
                                        totalColumns={deepNestedRow.cells.length}
                                        nestedLevel={nestedLevel + 1}
                                        selectedElement={selectedElement}
                                        selectedCell={selectedCell}
                                        onSelectElement={onSelectElement}
                                        onSelectNestedCell={onSelectNestedCell}
                                        onDeleteElement={onDeleteElement}
                                        onClearNestedCell={onClearNestedCell}
                                        onDeleteNestedColumn={onDeleteNestedColumn}
                                        onMoveNestedColumn={onMoveNestedColumn}
                                        onDragOver={onDragOver}
                                        onElementDragStart={onElementDragStart}
                                        onElementDrop={onElementDrop}
                                        onSelectCell={onSelectCell}
                                        onSelectRow={onSelectRow}
                                        previewMode={previewMode}
                                        previewMember={previewMember}
                                        demoAssets={demoAssets}
                                        isReadOnly={isReadOnly}
                                    />
                                ))}
                            </tr>
                            </tbody>
                        </table>
                    ))}
                </div>
            )}
        </td>
    );
}

function GridCell({
                      cell,
                      rowIndex,
                      cellIndex,
                      totalColumns,
                      isSelected,
                      selectedElement,
                      selectedCell,
                      onSelect,
                      onAddNestedGrid,
                      onClear,
                      onSelectElement,
                      onSelectNestedCell,
                      onDeleteElement,
                      onClearNestedCell,
                      onDeleteColumn,
                      onMoveColumn,
                      onDrop,
                      onDragOver,
                      onCellResize,
                      onElementDragStart,
                      onElementDrop,
                      onDeleteNestedColumn,
                      onMoveNestedColumn,
                      onAddNestedColumn,
                      onMoveElement,
                      onSelectCell,
                      onSelectRow,
                      previewMode,
                      previewMember,
                      demoAssets,
                      onDetachTemplate
                  }) {
    const [isResizing, setIsResizing] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    // Calculer les bordures personnalisées
    const cellBorderWidth = cell?.style?.borderWidth || '1px';
    const cellBorderColor = cell?.style?.borderColor || '#000000';
    const cellHasBorderTop = cell?.style?.borderTop;
    const cellHasBorderRight = cell?.style?.borderRight;
    const cellHasBorderBottom = cell?.style?.borderBottom;
    const cellHasBorderLeft = cell?.style?.borderLeft;
    const cellHasAnyBorder = cellHasBorderTop || cellHasBorderRight || cellHasBorderBottom || cellHasBorderLeft;

    const style = {
        paddingTop: cell?.style?.paddingTop || '15px',
        paddingRight: cell?.style?.paddingRight || '15px',
        paddingBottom: cell?.style?.paddingBottom || '15px',
        paddingLeft: cell?.style?.paddingLeft || '15px',
        verticalAlign: cell?.style?.verticalAlign || 'top',
        backgroundColor: cell?.style?.backgroundColor || undefined,
        width: cell?.style?.width || 'auto',
        position: 'relative',
        transition: isResizing ? 'none' : 'all 0.2s',
        // Bordures personnalisées
        borderTop: cellHasBorderTop ? `${cellBorderWidth} solid ${cellBorderColor}` : undefined,
        borderRight: cellHasBorderRight ? `${cellBorderWidth} solid ${cellBorderColor}` : undefined,
        borderBottom: cellHasBorderBottom ? `${cellBorderWidth} solid ${cellBorderColor}` : undefined,
        borderLeft: cellHasBorderLeft ? `${cellBorderWidth} solid ${cellBorderColor}` : undefined
    };

    // Protection contre les cellules undefined
    if (!cell) {
        return <td>Cellule non définie</td>;
    }

    // Fonction pour sélectionner un élément ET désélectionner la cellule/ligne
    const handleSelectElement = (element) => {
        if (onSelectCell) {
            onSelectCell(null); // Désélectionner la cellule
        }
        if (onSelectRow) {
            onSelectRow(null); // Désélectionner la ligne
        }
        onSelectElement(element);
    };

    const handleResizeStart = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const tdElement = e.target.closest('td');
        const initialX = e.clientX;
        const initialWidth = tdElement.offsetWidth;

        setIsResizing(true);

        const handleResizeMove = (moveEvent) => {
            const diff = moveEvent.clientX - initialX;
            const newWidthPx = Math.max(50, Math.round(initialWidth + diff));

            if (onCellResize) {
                onCellResize(rowIndex, cellIndex, newWidthPx);
            }
        };

        const handleResizeEnd = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', handleResizeEnd);
        };

        document.addEventListener('mousemove', handleResizeMove);
        document.addEventListener('mouseup', handleResizeEnd);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragOver(false);
        }
    };

    const handleDragOverLocal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver(e);
    };

    const handleElementDragStart = (e, element, sourceRowIndex, sourceCellIndex, sourceNestedRowIndex = null, sourceNestedCellIndex = null) => {
        e.stopPropagation();

        const dragData = {
            type: 'existing-element',
            element,
            sourceRowIndex,
            sourceCellIndex,
            sourceNestedRowIndex,
            sourceNestedCellIndex
        };

        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/json', JSON.stringify(dragData));

        if (onElementDragStart) {
            onElementDragStart(dragData);
        }

        e.target.classList.add('dragging');
    };

    const handleElementDragEnd = (e) => {
        e.target.classList.remove('dragging');
    };

    const handleCellDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        // Empêcher le drop si la cellule contient un template
        if (cell._templateMeta) {
            return;
        }

        try {
            const data = e.dataTransfer.getData('application/json');
            if (data) {
                const dragData = JSON.parse(data);

                if (dragData.type === 'existing-element' && onElementDrop) {
                    onElementDrop(dragData, rowIndex, cellIndex);
                    return;
                }
            }
        } catch (err) {}

        onDrop(e, rowIndex, cellIndex);
    };

    const handleCellClick = (e) => {
        // Ne pas sélectionner la cellule si on clique sur le resize-handle
        if (e.target.classList.contains('resize-handle')) {
            return;
        }
        onSelect(e);
    };

    return (
        <td
            className={`grid-cell ${isSelected && !previewMode ? 'selected' : ''} ${isDragOver && !previewMode ? 'drag-over' : ''} ${previewMode ? 'preview-mode' : ''}`}
            style={style}
            data-row={rowIndex}
            data-cell={cellIndex}
            onClick={!previewMode ? handleCellClick : undefined}
            onDragEnter={!previewMode ? handleDragEnter : undefined}
            onDragLeave={!previewMode ? handleDragLeave : undefined}
            onDragOver={!previewMode ? handleDragOverLocal : undefined}
            onDrop={!previewMode ? handleCellDrop : undefined}
        >
            {!previewMode && (
                <button
                    className="cell-selector-btn"
                    onClick={(e) => { e.stopPropagation(); onSelect(e); }}
                >
                    Cellule
                </button>
            )}

            {/* Afficher le resize-handle seulement si ce n'est pas la dernière cellule */}
            {!previewMode && cellIndex < totalColumns - 1 && (
                <div
                    className="resize-handle"
                    onMouseDown={handleResizeStart}
                    onClick={(e) => e.stopPropagation()}
                    title="Glisser pour redimensionner"
                />
            )}

            {!previewMode && (
                <div className="cell-actions">
                {/* Déplacement horizontal */}
                {cellIndex > 0 && (
                    <button
                        className="cell-action-btn"
                        onClick={(e) => { e.stopPropagation(); onMoveColumn('left'); }}
                        title="Déplacer à gauche"
                    >
                        ←
                    </button>
                )}
                {cellIndex < totalColumns - 1 && (
                    <button
                        className="cell-action-btn"
                        onClick={(e) => { e.stopPropagation(); onMoveColumn('right'); }}
                        title="Déplacer à droite"
                    >
                        →
                    </button>
                )}
                {/* Actions de cellule */}
                <button
                    className="cell-action-btn"
                    onClick={(e) => { e.stopPropagation(); onAddNestedGrid(); }}
                    title="Ajouter grille"
                >
                    ➕
                </button>
                <button
                    className="cell-action-btn danger"
                    onClick={(e) => { e.stopPropagation(); onClear(); }}
                    title="Vider"
                >
                    🗑️
                </button>
                <button
                    className="cell-action-btn danger"
                    onClick={(e) => { e.stopPropagation(); onDeleteColumn(); }}
                    title="Supprimer la colonne"
                >
                    ⊖
                </button>
                </div>
            )}

            {/* Template Cell Indicator */}
            {cell._templateMeta && !previewMode && (
                <TemplateCellIndicator
                    templateName={cell._templateMeta.templateName}
                    version={cell._templateMeta.version}
                    onDetach={onDetachTemplate}
                />
            )}

            {cell.elements?.length === 0 && !cell.nestedGrid && !previewMode && (
                <div className="drop-zone">Glissez un élément ici</div>
            )}

            {cell.elements?.map((element, elementIndex) => (
                <div
                    key={element.id}
                    className={`canvas-element ${selectedElement?.id === element.id && !previewMode ? 'selected' : ''} ${cell._templateMeta ? 'readonly' : ''}`}
                    draggable={!previewMode && !cell._templateMeta}
                    onDragStart={!previewMode && !cell._templateMeta ? (e) => handleElementDragStart(e, element, rowIndex, cellIndex) : undefined}
                    onDragEnd={!previewMode && !cell._templateMeta ? handleElementDragEnd : undefined}
                    onClick={!previewMode && !cell._templateMeta ? (e) => { e.stopPropagation(); handleSelectElement(element); } : undefined}
                    style={{ cursor: cell._templateMeta ? 'default' : undefined }}
                >
                    {!previewMode && <span className="element-label">{getElementTypeLabel(element.type)}</span>}
                    {!previewMode && !cell._templateMeta && cell.elements.length > 1 && (
                        <div className="element-move-btns">
                            {elementIndex > 0 && (
                                <button
                                    className="element-move-btn"
                                    onClick={(e) => { e.stopPropagation(); onMoveElement(rowIndex, cellIndex, elementIndex, 'up'); }}
                                    title="Monter"
                                >
                                    ↑
                                </button>
                            )}
                            {elementIndex < cell.elements.length - 1 && (
                                <button
                                    className="element-move-btn"
                                    onClick={(e) => { e.stopPropagation(); onMoveElement(rowIndex, cellIndex, elementIndex, 'down'); }}
                                    title="Descendre"
                                >
                                    ↓
                                </button>
                            )}
                        </div>
                    )}
                    {!previewMode && !cell._templateMeta && (
                        <button
                            className="element-delete"
                            onClick={(e) => { e.stopPropagation(); onDeleteElement(element.id); }}
                        >
                            ×
                        </button>
                    )}
                    <ElementRenderer element={element} previewMode={previewMode} previewMember={previewMember} demoAssets={demoAssets} />
                </div>
            ))}

            {cell.nestedGrid && (
                <div style={{
                    marginTop: previewMode ? '0' : '10px',
                    border: previewMode ? 'none' : '1px dashed #ccc',
                    padding: previewMode ? '0' : '10px',
                    borderRadius: previewMode ? '0' : '4px',
                    background: previewMode ? 'transparent' : '#fafafa'
                }}>
                    {cell.nestedGrid.map((nestedRow, nestedIndex) => (
                        <div key={nestedIndex} style={{ marginBottom: previewMode ? '0' : '8px', position: 'relative' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                <tr>
                                    {nestedRow.cells.map((nestedCell, nestedCellIndex) => (
                                        <NestedGridCell
                                            key={nestedCellIndex}
                                            cell={nestedCell}
                                            parentRowIndex={rowIndex}
                                            parentCellIndex={cellIndex}
                                            nestedRowIndex={nestedIndex}
                                            nestedCellIndex={nestedCellIndex}
                                            totalColumns={nestedRow.cells.length}
                                            selectedElement={selectedElement}
                                            selectedCell={selectedCell}
                                            onSelectElement={onSelectElement}
                                            onSelectNestedCell={onSelectNestedCell}
                                            onDeleteElement={onDeleteElement}
                                            onClearNestedCell={onClearNestedCell}
                                            onDeleteNestedColumn={onDeleteNestedColumn}
                                            onMoveNestedColumn={onMoveNestedColumn}
                                            onDragOver={onDragOver}
                                            onElementDragStart={handleElementDragStart}
                                            onElementDrop={onElementDrop}
                                            onSelectCell={onSelectCell}
                                            onSelectRow={onSelectRow}
                                            previewMode={previewMode}
                                            previewMember={previewMember}
                                            demoAssets={demoAssets}
                                            isReadOnly={!!cell._templateMeta}
                                        />
                                    ))}
                                </tr>
                                </tbody>
                            </table>
                            {/* Bouton pour ajouter une colonne */}
                            {!previewMode && !cell._templateMeta && nestedRow.cells.length < 4 && (
                                <button
                                    className="add-nested-column-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onAddNestedColumn) {
                                            onAddNestedColumn(rowIndex, cellIndex, nestedIndex);
                                        }
                                    }}
                                    title="Ajouter une colonne"
                                    style={{
                                        position: 'absolute',
                                        right: '-35px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: '#27ae60',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-50%) scale(1.1)'}
                                    onMouseLeave={(e) => e.target.style.transform = 'translateY(-50%) scale(1)'}
                                >
                                    ➕
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </td>
    );
}

export default GridCell;
